import {
  type DiagnosisFieldsPayload,
  type ExtendedDiagnosisFields,
} from '@/legacy-src/components/diagnosis/client/types'
import { type TherapistPriceType } from '@/legacy-src/types/TherapistStateType'
import { getStringFromHash } from '@/legacy-src/utilities/codingPublicHash'

export const normalizeDiagnosisData = (data: ExtendedDiagnosisFields): DiagnosisFieldsPayload => {
  return {
    ...data,
    lead: data.lead * 5,
    emotions: data.emotions * 3,
    past: data.past * 4,
    personality: data.personality * 5,
    specialization: data.specialization.map((item) => parseInt(item, 10)),
    services: [data.service],
    sessionType: data.sessionType,
    sessionOfficeDistrictIds: data.sessionLocation,
    therapyActivities: data.therapyActivities?.length
      ? data.therapyActivities.map((item) => parseInt(item, 10))
      : undefined,
    // these are temporary overrides since we have several processing functions
    // the proper fix would be to use the same data processing in both sending diagnosis and diagnosis preview
    ...(typeof data.therapistAgeGroup === 'string' ? { therapistAgeGroup: parseInt(data.therapistAgeGroup, 10) } : {}),
    ...(typeof data.sex === 'string' ? { sex: parseInt(data.sex, 10) } : {}),
    ...(typeof data.maxPrice === 'string' ? { maxPrice: parseInt(data.maxPrice, 10) } : {}),
  }
}

export const normalizeDiagnosisDataV2 = (data: ExtendedDiagnosisFields) => {
  return {
    ...normalizeDiagnosisData(data),
    therapistAgeGroup: parseInt(data.therapistAgeGroup, 10),
  }
}

export const parseDiagnosisParams = (paramsString: string) => {
  try {
    const paramsJson = getStringFromHash(paramsString as string)
    const params = paramsJson ? JSON.parse(paramsJson) : null
    return paramsJson ? params : null
  } catch {
    return null
  }
}

export const getPricePoints = (therapists, selectedServiceId: number) => {
  const serviceMatchinTherapist = therapists.filter(
    (therapist) => therapist.services?.length && therapist.services.includes(selectedServiceId),
  )
  const pricesMatchingService = serviceMatchinTherapist
    .map((therapist) => therapist.prices?.find((price: TherapistPriceType) => price.serviceId === selectedServiceId))
    .filter((price) => price !== undefined)

  const priceOccurrences: { [key: number]: number } = {}
  pricesMatchingService.forEach((item) => {
    const price = item.price
    priceOccurrences[price] = (priceOccurrences[price] || 0) + 1
  })

  // Exclude prices that appear only once
  return pricesMatchingService

    .filter((item) => priceOccurrences[item.price] > 1)
    .map((item) => item.price)
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort((a, b) => a - b)
}

export const isNewDiagnosisExtendedData = (data) => {
  return Number.isInteger(data?.therapistAgeGroup)
}

export const hasDiagnosisFilledOut = (diagnosis) => diagnosis && Object.keys(diagnosis).length > 0
export const isDiagnosisEmpty = (diagnosis) => diagnosis && Object.keys(diagnosis).length === 0

export const parseDefaultValues = (params: string) => {
  try {
    const paramsJson = getStringFromHash(params)
    return paramsJson ? JSON.parse(paramsJson) : {}
  } catch {
    return {}
  }
}

export const genderList = (t) => [
  {
    label: t('diagnosisTest.sex.0'),
    value: '0',
    icon: 'gender-man',
  },
  {
    label: t('diagnosisTest.sex.1'),
    value: '1',
    icon: 'gender-woman',
  },
  {
    label: t('diagnosisTest.sex.2'),
    value: '2',
    icon: 'gender-both',
  },
]

export const ageList = (t) => [
  {
    label: t('diagnosisTest.age.0'),
    value: 0,
  },
  {
    label: t('diagnosisTest.age.1'),
    value: 1,
  },
  {
    label: t('diagnosisTest.age.2'),
    value: 2,
  },
  {
    label: t('diagnosisTest.age.3'),
    value: 3,
  },
]
